import { useContext, type MouseEvent } from "react";
import { useLocation } from "react-router-dom";
import { GuideModalContext, type GuideModalApi } from "@/lib/guide-modal-context";
import { GUIDE_OPENED_STATE, withGuide } from "@/lib/guide-stepper";

/** The guide modal, or null outside the portal shell (tests, login pages). */
export function useGuideModal(): GuideModalApi | null {
  return useContext(GuideModalContext);
}

/** A plain left click (no modifier). Ctrl/⌘/middle click keep the link's own
 *  behaviour (the same page with the guide open, in a new tab). */
export function plainClick(e: Pick<MouseEvent, "button" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey">): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/**
 * Props for a link that opens a guide: the current page with `?guide=<key>`
 * added — following it opens the guide modal over the page (no route change).
 * `before` runs first (dismiss a banner).
 */
export function useGuideLink() {
  const { pathname, search, hash } = useLocation();
  return (key: string, before?: () => void) => ({
    to: { pathname, search: withGuide(search, key), hash },
    state: GUIDE_OPENED_STATE,
    onClick: () => before?.(),
  });
}
